import React, { useEffect, useState } from 'react';
import { ReactComponent as LoadingSpinner } from 'images/loader.svg';
import { useDispatch, useSelector } from 'react-redux';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { useQueryParams } from 'hooks/useQueryParam';
import { authSessionThunk, getLoanOffer, getStudentLoanAssistanceData, getApplicationData } from 'thunks';
import { ConversionType, trackConversion, trackConversionLead } from 'utils/analytics';
import { getUtmTagsVariables } from 'utils/getUtmTags';
import StateContainer from 'components/StateContainer';
import { getYourContactData } from 'selectors/getYourContact';
import { getYourNameData } from 'selectors/yourName';
import { getMethodAuthData } from 'selectors/methodAuth';
import { FlowComponentType } from 'routes/types';
import { useNavigationType } from 'hooks/useNavigate';
import { getAuthData } from 'selectors/getAuthData';
import { CurrentFlow } from 'enums/CurrentFlow';
import { LoanOfferProgress, LoanOfferStep } from 'api/LoanOfferApi';
import { ResultStage } from 'api/StudentLoanApi';
import { StudentLoanEligibilityResult as Result } from 'enums/StudentLoanForgivenessFlowResults';
import { getStudentLoanData } from 'selectors/getStudentLoanData';
import { setStudentLoanApplicationId } from 'handlers/studentLoanData';
import RequestError from 'errors/RequestError';
import { getLoanOfferPayload } from 'selectors/getLoanOfferData';

import styles from './Analyzing.module.scss';

const STEPS_READY_FOR_STUDENT_LOANS = [
  LoanOfferStep.RunningDecisionEngine,
  LoanOfferStep.GeneratingDebtProfile,
  LoanOfferStep.CreatingHardOffer,
];

type StudentLoanAssistanceInputData = {
  ready: boolean;
  applicationId?: string;
};

export const getLoadingProgress = (offerProgress: LoanOfferProgress) => {
  let progress;
  switch (offerProgress.step) {
    case LoanOfferStep.ValidatedInput:
      progress = 30;
      break;
    case LoanOfferStep.GettingTradelines:
      progress = 30;
      break;
    case LoanOfferStep.SyncingTradelines:
      if (offerProgress.initialAccounts && offerProgress.syncingAccounts !== undefined) {
        progress = 100 - (50 * offerProgress.syncingAccounts) / offerProgress.initialAccounts;
      } else {
        progress = 30;
      }
      break;
    case LoanOfferStep.RunningDecisionEngine:
    case LoanOfferStep.GeneratingDebtProfile:
    case LoanOfferStep.CreatingHardOffer:
      progress = 100;
      break;
    default:
      progress = 0;
  }
  return progress;
};

const Analyzing = ({ handleNext }: FlowComponentType): JSX.Element => {
  const params = useQueryParams();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const dispatch = useDispatch();
  const navigationType = useNavigationType();

  const [progress, setProgress] = useState(0);

  const { email, phone_number: phoneNumber } = useSelector(getYourContactData);
  const { first_name: firstName, last_name: lastName } = useSelector(getYourNameData);
  const loanOfferPayload = useSelector(getLoanOfferPayload);

  const { entityId, isLoading } = useSelector(getMethodAuthData);
  const { sessionToken } = useSelector(getAuthData);
  const { isLoading: isLoadingStudentLoanData, fetched: studentLoanDataFetched, result } = useSelector(
    getStudentLoanData,
  );
  const [studentLoanAssistanceInput, setStudentLoanAssistanceInput] = useState<StudentLoanAssistanceInputData>({
    ready: false,
  });

  // Create financial checkup application
  useEffect(() => {
    const apply = async () => {
      if (!entityId && isLoading) {
        return;
      }

      if (!entityId && !isLoading) {
        if (!sessionToken) {
          handleNext(Result.Error);
          return;
        }
        dispatchWithUnwrap(
          authSessionThunk({
            phoneNumber,
            firstName,
            lastName,
            sessionToken,
          }),
        );
        return;
      }

      try {
        setProgress(10);

        const offer = await dispatchWithUnwrap(
          getLoanOffer({
            ...loanOfferPayload,
            // StudentLoanForgiveness uses AICoach flow
            currentFlow: CurrentFlow.AICoach,
            utm: getUtmTagsVariables(params),
            updateCallback: loanOfferUpdateCallback,
          }),
        );

        if (offer.data.borrower_id) {
          analytics.identify(offer.data.borrower_id);
          (window as any).nid('setUserId', offer.data.application_id);
        }

        const applicationId = offer.data.application_id!;
        setStudentLoanAssistanceInput({ ready: true, applicationId });
        dispatch(setStudentLoanApplicationId(applicationId));
        try {
          await dispatchWithUnwrap(getApplicationData(applicationId));
        } catch (error) {
          analytics.track('Failed to load application after student loan offer', { error, applicationId });
        }
      } catch (error: unknown) {
        if ((error as RequestError<unknown>).responseStatus === 401) {
          handleNext(Result.SessionExpired);
        } else {
          handleNext(Result.Error);
        }
        return;
      }

      trackConversionLead({
        email,
        firstName,
        lastName,
        phoneNumber,
      });

      trackConversion(ConversionType.StudentLoanApplied);
    };

    if (navigationType === 'POP') {
      return;
    }
    apply();
  }, [entityId]);

  // When creating a loan offer, we intercept the update callback to determine if application is ready to get student loan assistance
  const loanOfferUpdateCallback = async (data: LoanOfferProgress) => {
    setProgress(getLoadingProgress(data));
    if (data.applicationId && STEPS_READY_FOR_STUDENT_LOANS.includes(data.step)) {
      setStudentLoanAssistanceInput({ ready: true, applicationId: data.applicationId });
    }
  };

  // Fetch student loan assistance data for application
  const fetchStudentLoanAssistanceData = async () => {
    if (studentLoanAssistanceInput.ready && studentLoanAssistanceInput.applicationId) {
      try {
        await dispatchWithUnwrap(
          getStudentLoanAssistanceData({ applicationId: studentLoanAssistanceInput.applicationId }),
        );
      } catch {
        handleNext(Result.Error);
      }
    }
  };

  // Fetch student loan assistance data for application when application data is available
  useEffect(() => {
    if (!isLoadingStudentLoanData && !studentLoanDataFetched && studentLoanAssistanceInput.ready) {
      fetchStudentLoanAssistanceData();
    }
  }, [studentLoanAssistanceInput]);

  useEffect(() => {
    if (result) {
      handleNext(result.stage === ResultStage.NoFederalLoans ? Result.NotEligible : Result.Eligible);
    }
  }, [result]);

  return (
    <div className={styles.container}>
      <StateContainer icon={<LoadingSpinner />} title="Analyzing Student Loans..." progress={progress} />
    </div>
  );
};

export default Analyzing;
